import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { addExpense, addTransaction } from './financeService';

export interface PettyCashExpense {
  id: string;
  date: Date | Timestamp;
  amount: number;
  category: string;
  description: string;
  vendor?: string;
  paymentMethod?: string;
  ref?: string;
  submissionStatus: 'draft' | 'pending' | 'checked' | 'approved' | 'rejected';
  submissionPeriod?: 'daily' | 'weekly' | 'monthly';
  submittedAt?: Date | Timestamp;
  submittedBy?: string;
  checkedBy?: string;
  checkedAt?: Date | Timestamp;
  approvedBy?: string;
  approvedAt?: Date | Timestamp;
  rejectedBy?: string;
  rejectedAt?: Date | Timestamp;
  rejectionReason?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

/**
 * Get all petty cash expenses
 */
export const fetchPettyCashExpenses = async (
  companyId: string,
  status?: 'draft' | 'pending' | 'checked' | 'approved' | 'rejected'
): Promise<PettyCashExpense[]> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expensesRef = collection(financeDocRef, 'pettyCashExpenses');
  
  let q = query(expensesRef, orderBy('createdAt', 'desc'));
  
  if (status) {
    q = query(q, where('submissionStatus', '==', status));
  }
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate ? data.date.toDate() : new Date(data.date),
      submittedAt: data.submittedAt?.toDate ? data.submittedAt.toDate() : data.submittedAt ? new Date(data.submittedAt) : undefined,
      checkedAt: data.checkedAt?.toDate ? data.checkedAt.toDate() : data.checkedAt ? new Date(data.checkedAt) : undefined,
      approvedAt: data.approvedAt?.toDate ? data.approvedAt.toDate() : data.approvedAt ? new Date(data.approvedAt) : undefined,
      rejectedAt: data.rejectedAt?.toDate ? data.rejectedAt.toDate() : data.rejectedAt ? new Date(data.rejectedAt) : undefined,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
    } as PettyCashExpense;
  });
};

/**
 * Add a petty cash expense (draft)
 */
export const addPettyCashExpense = async (
  companyId: string,
  expenseData: Omit<PettyCashExpense, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'submissionStatus'>
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expensesRef = collection(financeDocRef, 'pettyCashExpenses');
  const expenseRef = doc(expensesRef);
  
  let expenseDate: Date;
  if (expenseData.date instanceof Date) {
    expenseDate = expenseData.date;
  } else if (expenseData.date && typeof (expenseData.date as any).toDate === 'function') {
    expenseDate = (expenseData.date as any).toDate();
  } else if (expenseData.date && typeof expenseData.date === 'object' && (expenseData.date as any).seconds) {
    const ts = expenseData.date as Timestamp;
    expenseDate = ts.toDate();
  } else {
    expenseDate = new Date(expenseData.date as any);
  }

  const expense: any = {
    amount: expenseData.amount,
    category: expenseData.category,
    description: expenseData.description,
    submissionStatus: 'draft',
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    date: Timestamp.fromDate(expenseDate),
  };

  if (expenseData.vendor && expenseData.vendor.trim()) {
    expense.vendor = expenseData.vendor;
  }
  if (expenseData.paymentMethod && expenseData.paymentMethod.trim()) {
    expense.paymentMethod = expenseData.paymentMethod;
  }
  if (expenseData.ref && expenseData.ref.trim()) {
    expense.ref = expenseData.ref;
  }

  await setDoc(expenseRef, expense);
  return expenseRef.id;
};

/**
 * Update a petty cash expense
 */
export const updatePettyCashExpense = async (
  companyId: string,
  expenseId: string,
  expenseData: Partial<Omit<PettyCashExpense, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(financeDocRef, 'pettyCashExpenses', expenseId);
  
  const updateData: any = {
    updatedAt: serverTimestamp(),
  };

  if (expenseData.date) {
    let expenseDate: Date;
    if (expenseData.date instanceof Date) {
      expenseDate = expenseData.date;
    } else if (expenseData.date && typeof (expenseData.date as any).toDate === 'function') {
      expenseDate = (expenseData.date as any).toDate();
    } else if (expenseData.date && typeof expenseData.date === 'object' && (expenseData.date as any).seconds) {
      const ts = expenseData.date as Timestamp;
      expenseDate = ts.toDate();
    } else {
      expenseDate = new Date(expenseData.date as any);
    }
    updateData.date = Timestamp.fromDate(expenseDate);
  }

  if (expenseData.amount !== undefined) updateData.amount = expenseData.amount;
  if (expenseData.category !== undefined) updateData.category = expenseData.category;
  if (expenseData.description !== undefined) updateData.description = expenseData.description;
  if (expenseData.vendor !== undefined) updateData.vendor = expenseData.vendor;
  if (expenseData.paymentMethod !== undefined) updateData.paymentMethod = expenseData.paymentMethod;
  if (expenseData.ref !== undefined) updateData.ref = expenseData.ref;
  if (expenseData.submissionStatus !== undefined) updateData.submissionStatus = expenseData.submissionStatus;
  if (expenseData.submissionPeriod !== undefined) updateData.submissionPeriod = expenseData.submissionPeriod;

  await updateDoc(expenseRef, updateData);
};

/**
 * Submit expenses for review
 */
export const submitPettyCashExpenses = async (
  companyId: string,
  expenseIds: string[],
  period: 'daily' | 'weekly' | 'monthly'
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expensesRef = collection(financeDocRef, 'pettyCashExpenses');
  const batch = writeBatch(db);

  expenseIds.forEach((expenseId) => {
    const expenseRef = doc(expensesRef, expenseId);
    batch.update(expenseRef, {
      submissionStatus: 'pending',
      submissionPeriod: period,
      submittedAt: serverTimestamp(),
      submittedBy: user.uid,
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
};

/**
 * Check an expense (mark as checked)
 */
export const checkPettyCashExpense = async (
  companyId: string,
  expenseId: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(financeDocRef, 'pettyCashExpenses', expenseId);
  
  await updateDoc(expenseRef, {
    submissionStatus: 'checked',
    checkedBy: user.uid,
    checkedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Approve an expense (adds to expenses and transactions)
 */
export const approvePettyCashExpense = async (
  companyId: string,
  expenseId: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(financeDocRef, 'pettyCashExpenses', expenseId);
  const expenseDoc = await getDoc(expenseRef);
  
  if (!expenseDoc.exists()) {
    throw new Error('Expense not found');
  }

  const expenseData = expenseDoc.data() as PettyCashExpense;
  
  // Update status to approved
  await updateDoc(expenseRef, {
    submissionStatus: 'approved',
    approvedBy: user.uid,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Add to regular expenses collection
  await addExpense(companyId, {
    date: expenseData.date,
    amount: expenseData.amount,
    category: expenseData.category,
    description: expenseData.description,
    vendor: expenseData.vendor,
    paymentMethod: expenseData.paymentMethod,
    ref: expenseData.ref,
    status: 'paid',
  });
};

/**
 * Reject an expense
 */
export const rejectPettyCashExpense = async (
  companyId: string,
  expenseId: string,
  reason: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(financeDocRef, 'pettyCashExpenses', expenseId);
  
  await updateDoc(expenseRef, {
    submissionStatus: 'rejected',
    rejectedBy: user.uid,
    rejectedAt: serverTimestamp(),
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a petty cash expense (only if draft)
 */
export const deletePettyCashExpense = async (
  companyId: string,
  expenseId: string
): Promise<void> => {
  const financeDocRef = doc(db, 'companies', companyId, 'finance', 'data');
  const expenseRef = doc(financeDocRef, 'pettyCashExpenses', expenseId);
  await deleteDoc(expenseRef);
};
